import { useState, type FormEvent } from 'react';
import { ApiError, reportTheme } from '../api/client';
import { REPORT_REASONS, type ReportReason } from '../api/types';

/**
 * 「この診断を通報する」（doc/ADS.md §5）。診断の入口と結果ページの下に置く。ログイン不要。
 *
 * 押すと理由とひとことの欄が開く。送ると、その診断には管理者が見るまで広告が出なくなる（消すかどうかは管理者が決める）。
 * 送った人は記録しない、と欄の下に書く。
 */
export function ReportTheme({ themeId, resultId }: { readonly themeId: string; readonly resultId?: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState('');

  if (state === 'done') {
    return <p className="report report--done" role="status">通報を受け付けました。運営が確かめます。ありがとうございました。</p>;
  }

  if (!open) {
    return (
      <p className="report">
        <button className="report__open" type="button" onClick={() => setOpen(true)}>この診断を通報する</button>
      </p>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!reason) return;
    setState('sending');
    setError('');
    void reportTheme(themeId, { reason, note, ...(resultId ? { resultId } : {}) })
      .then(() => setState('done'))
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : '送れませんでした。時間をおいてお試しください。');
        setState('idle');
      });
  };

  return (
    <form className="report report--open" onSubmit={submit}>
      <fieldset className="report__reasons" disabled={state === 'sending'}>
        <legend className="field__label">どこが問題ですか</legend>
        {REPORT_REASONS.map((item) => (
          <label key={item.id} className="field field--check">
            <input type="radio" name="report-reason" checked={reason === item.id} onChange={() => setReason(item.id)} />
            <span>{item.label}</span>
          </label>
        ))}
      </fieldset>
      <label className="field">
        <span className="field__label">ひとこと（任意・300 字まで）</span>
        <textarea className="field__input" rows={2} maxLength={300} value={note} disabled={state === 'sending'} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error && <p className="page__error" role="alert">{error}</p>}
      <div className="page__actions">
        <button className="button button--danger button--small" type="submit" disabled={!reason || state === 'sending'}>
          {state === 'sending' ? '送っています…' : '通報する'}
        </button>
        <button className="button button--ghost button--small" type="button" onClick={() => setOpen(false)} disabled={state === 'sending'}>
          やめる
        </button>
      </div>
      <p className="page__footnote">
        ログインは要りません。<strong>送った人は記録しません</strong>（同じブラウザからの送りすぎを数えるためだけに、日付を混ぜた符号を 3 日持ちます）。
        運営が確かめるまで、この診断には広告を出しません。
      </p>
    </form>
  );
}
